import { useEffect, useState } from "react";
import { ShieldCheck, Cpu, Coins, Activity } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { PageHeader } from "../components/Shared";

const usd = (v) => `$${Number(v || 0).toFixed(v >= 1 ? 2 : 4)}`;
const num = (v) => new Intl.NumberFormat("en-GB").format(Number(v || 0));
const FEATURE = { invoice_extraction: "Invoice reading", product_matching: "AI product matching", invoice_splitting: "PDF splitting", statement_extraction: "Statement reading", market_notes: "Market notes" };
const RANGES = [7, 30, 90, 365];

function Kpi({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 fade-up" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider"><Icon className="h-4 w-4" /> {label}</div>
      <p className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-2 font-mono">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Table({ title, rows, cols, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden fade-up" data-testid={testId}>
      <h2 className="text-base font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">{title}</h2>
      {rows.length === 0 ? <p className="px-4 py-4 text-sm text-slate-500">No usage in this period.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>{cols.map((c) => <th key={c.key} className={`px-4 py-2 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={i}>{cols.map((c) => <td key={c.key} className={`px-4 py-2 ${c.right ? "text-right font-mono" : ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DayBars({ rows }) {
  const max = Math.max(0.0001, ...rows.map((r) => r.cost_usd));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 fade-up" data-testid="admin-day-chart">
      <h2 className="text-base font-semibold text-slate-900 mb-3">Daily cost</h2>
      {rows.length === 0 ? <p className="text-sm text-slate-500">No usage in this period.</p> : (
        <div className="flex items-end gap-1 h-32">
          {rows.map((r) => (
            <div key={r.day} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-[6px]" title={`${r.day}: ${usd(r.cost_usd)} · ${r.calls} calls`}>
              <div className="w-full rounded-t bg-emerald-500" style={{ height: `${Math.max(4, (r.cost_usd / max) * 100)}px` }} />
              {rows.length <= 14 && <span className="text-[9px] text-slate-400 font-mono">{r.day.slice(5)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/ai-usage", { params: { days } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [days]);

  const costCols = [
    { key: "calls", label: "Calls", right: true, render: (r) => num(r.calls) },
    { key: "input_tokens", label: "Input tokens", right: true, render: (r) => num(r.input_tokens) },
    { key: "output_tokens", label: "Output tokens", right: true, render: (r) => num(r.output_tokens) },
    { key: "cost_usd", label: "Cost", right: true, render: (r) => usd(r.cost_usd) },
  ];

  return (
    <div data-testid="admin-page">
      <PageHeader title="Admin · AI usage" subtitle="Who uses the AI, where, and what it costs (OpenAI list prices, USD)."
        actions={<div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="admin-range">{RANGES.map((d) => <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${days === d ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`admin-range-${d}`}>{d}d</button>)}</div>} />
      {!data ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={Coins} label="Total cost" value={usd(data.totals.cost_usd)} sub={`last ${data.days} days`} testId="admin-kpi-cost" />
            <Kpi icon={Activity} label="AI calls" value={num(data.totals.calls)} testId="admin-kpi-calls" />
            <Kpi icon={Cpu} label="Input tokens" value={num(data.totals.input_tokens)} testId="admin-kpi-input" />
            <Kpi icon={Cpu} label="Output tokens" value={num(data.totals.output_tokens)} testId="admin-kpi-output" />
          </div>
          <DayBars rows={data.by_day} />
          <Table title="By user" testId="admin-by-user" rows={data.by_user} cols={[
            { key: "name", label: "User", render: (r) => <span><span className="font-medium text-slate-900">{r.name}</span>{r.email && <span className="block text-xs text-slate-500">{r.email}{r.role ? ` · ${r.role}` : ""}</span>}</span> },
            { key: "organisation", label: "Organisation" }, ...costCols]} />
          <div className="grid lg:grid-cols-2 gap-6">
            <Table title="By organisation" testId="admin-by-org" rows={data.by_org} cols={[{ key: "organisation", label: "Organisation" }, ...costCols]} />
            <Table title="By feature" testId="admin-by-feature" rows={data.by_feature} cols={[{ key: "feature", label: "Feature", render: (r) => FEATURE[r.feature] || r.feature }, ...costCols]} />
          </div>
          <Table title="By model" testId="admin-by-model" rows={data.by_model} cols={[
            { key: "model", label: "Model", render: (r) => <span className="font-mono">{r.model}<span className="block text-xs text-slate-500 font-sans">{data.pricing[r.model] ? `$${data.pricing[r.model].input_per_1m} in · $${data.pricing[r.model].output_per_1m} out per 1M tokens` : "price unknown"}</span></span> }, ...costCols]} />
          <Table title="Recent calls" testId="admin-recent" rows={data.recent} cols={[
            { key: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) },
            { key: "user_name", label: "User", render: (r) => <span>{r.user_name}<span className="block text-xs text-slate-500">{r.organisation}</span></span> },
            { key: "feature", label: "Feature", render: (r) => <span>{FEATURE[r.feature] || r.feature}<span className="block text-xs text-slate-500">{r.detail}{r.status !== "ok" ? " · failed" : ""}</span></span> },
            { key: "model", label: "Model", render: (r) => <span className="font-mono text-xs">{r.model}</span> },
            { key: "total_tokens", label: "Tokens", right: true, render: (r) => num(r.total_tokens) },
            { key: "cost_usd", label: "Cost", right: true, render: (r) => usd(r.cost_usd) }]} />
          <p className="text-xs text-slate-400 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Costs are estimates from OpenAI list prices; your Emergent Universal Key balance is the source of truth.</p>
        </div>
      )}
    </div>
  );
}
